import * as css from 'css/exports.module.scss';
import './UptimeMonitorsDashboard.scss';
import { createAsyncSelectors } from '@lib/selectors';
import dayjs from 'dayjs';
import { FeatureBoundary } from '@ui/features';
import { getDirectParent } from '@lib/popups';
import { InfoCircleOutlined } from '@ant-design/icons';
import { SonarText } from '@ui/text';
import { Column } from '@ant-design/plots';
import { coreActions, DataSecurityMonitorType, FeatureFlag } from '@sonar-software/sonar-core';
import { DATA_SECURITY_DASHBOARD, FORMATS } from '@sonar-software/sonar-ts-constants';
import React, { memo, useMemo } from 'react';
import { SonarCard, StatisticDelta } from '@ui/general';
import { Space, Statistic, Tooltip } from 'antd';
import { useAsyncStateWithPreload, useDataSecurityApps } from '@hooks';
const dashboardSelectors = createAsyncSelectors((state) => state.core.dataSecurity.monitorsDashboard.get);
function mapDowntimeDays(downtimeDays, uptimeInterval) {
    const days = [];
    for (let index = 0; index < uptimeInterval; index++) {
        const down = downtimeDays.find((x) => x === index + 1);
        days.push({
            color: down ? css.deletion : css.lightGreen,
            day: index,
            up: down ? 50 : 100
        });
    }
    return days;
}
const chartConfig = {
    autoFit: true,
    color: ({ color }) => color,
    columnWidthRatio: 0.9,
    coordinate: [{
            type: 'reflectX'
        }],
    data: [],
    height: 50,
    legend: false,
    padding: 0,
    seriesField: 'color',
    slider: false,
    tooltip: {
        customContent: (day) => React.createElement("div", { className: 'tooltip-container' },
            React.createElement(Tooltip, { autoAdjustOverflow: false, getPopupContainer: getDirectParent, open: true, placement: 'bottom', title: `${dayjs().subtract(Number(day), 'day')
                    .format(FORMATS.SLASH_DATE)}` })),
        position: 'top'
    },
    xAxis: {
        label: {
            formatter: () => null
        },
        tickLine: { length: 0 }
    },
    xField: 'day',
    yAxis: {
        grid: null,
        label: {
            formatter: () => null
        }
    },
    yField: 'up'
};
export const UptimeMonitorsDashboard = memo(({ applicationId }) => {
    const apps = useDataSecurityApps();
    const hasUptimeMonitors = useMemo(() => (applicationId ? apps.filter((app) => app.applicationId === applicationId) : apps).some((app) => app.monitors.some((monitor) => monitor.monitorTypeId === DataSecurityMonitorType.Uptime)), [applicationId, apps]);
    const { data, isRequested } = useAsyncStateWithPreload(dashboardSelectors, coreActions.requestGetCoreDataSecurityUptimeMonitorsDashboard, true, { applicationId });
    if (!hasUptimeMonitors) {
        return null;
    }
    return (React.createElement(FeatureBoundary, { feature: FeatureFlag.DataSecurityDashboard },
        React.createElement(SonarCard, { id: 'uptime-montitors-dashboard', loading: isRequested, title: DATA_SECURITY_DASHBOARD.UPTIME_MONITORS, titleClassName: 'h2', useSpinnerLoading: true },
            React.createElement("div", { className: 'monitor-periods' }, data === null || data === void 0 ? void 0 : data.map((monitor) => React.createElement("div", { className: 'monitor-period', key: monitor.uptimeInterval },
                React.createElement(Statistic, { suffix: React.createElement(StatisticDelta, { change: monitor.changePercentage, isPercent: true, precision: 2 }), title: React.createElement(Space, { size: 8 },
                        React.createElement(SonarText, { className: 'monitor-title' }, DATA_SECURITY_DASHBOARD.UPTIME_DAYS(monitor.uptimeInterval)),
                        React.createElement(Tooltip, { title: DATA_SECURITY_DASHBOARD.UPTIME_TOOLTIP },
                            React.createElement(InfoCircleOutlined, null))), value: monitor.uptimePercentage }),
                React.createElement(Column, Object.assign({}, chartConfig, { data: mapDowntimeDays(monitor.downtimeDays, monitor.uptimeInterval) }))))))));
});
